import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, DoCheck, IterableDiffers, IterableDiffer } from '@angular/core';
import { DragulaService } from 'ng2-dragula';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Filter } from 'src/app/reports/components/report/models/filter.model';
import { Column } from 'src/app/reports/components/report/models/column.model';
import { RelatedEntity } from 'src/app/reports/components/report/models/related-entity.model';
import { ReportEntity } from 'src/app/reports/components/report/models/report-entity.model';
import { Report } from 'src/app/reports/components/report/models/report.model';
import { RelevantProperty, TypeEnum, newInstanceToObject, compareOnlyRelevantProperties } from 'src/app/reports/helpers/data.helper';
import { BaseComponent } from 'src/app/reports/shared/utils/base.component';
import { FilterTypeEnum } from 'src/app/reports/enums/filter-type.enum';
import { sortListByOrder } from 'src/app/reports/helpers/column.helper';
import { RTEAlertService } from 'rte-library';
import { EntityTypeEnum } from 'src/app/reports/enums/entity-type.enum';
import { TotalizerTypeEnum } from 'src/app/reports/enums/column-type.enum';

@Component({
  animations: [
    trigger('relation-visibility', [
      state('fullscreen', style({
        'max-width': '100%'
      })),
      state('default', style({
        'max-width': '65%'
      })),
      transition('* <=> *',  animate('300ms 0s ease-in-out'))
    ]),
    trigger('side-columns-visibility', [
      state('fullscreen', style({
        'min-width': '0%',
        'width': '0%'
      })),
      state('default', style({
        'max-width': '35%'
      })),
      transition('* <=> *',  animate('300ms 0s ease-in-out'))
    ])
  ],
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss']
})
export class BoardComponent extends BaseComponent implements OnInit, OnDestroy, DoCheck {

  //#region Inputs

  @Input() fullScreenState = 'default';
  @Input() sessionId: string;
  @Input() disabled = false;
  @Input() report: Report;
  @Input() entities: Array<ReportEntity>;
  @Input() verifyChanges: boolean;
  @Input() changes: number;

  //#endregion

  //#region Outputs

  @Output() onChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changesChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() fullScreenStateChange: EventEmitter<string> = new EventEmitter<string>();

  // Enable Two Way Data Biding

  //#endregion

  //#region Propreties

  relevantProperties: Array<RelevantProperty> = [];
  reportBKP: Report;

  entityDropModelChanged = false;

  expandFilter = true;
  expandEntity = true;
  expandColumn = true;

  columnDragulaGroup = '';
  linkGroupName = '';

  differFilters: IterableDiffer<any>;
  differColumns: IterableDiffer<any>;

  filters: Array<Filter> = new Array<Filter>();
  columns: Array<Column> = new Array<Column>();

  cdkDragDisabled = false;

  entitiesEntity: Array<ReportEntity>;

  //#endregion

  //#region Ctor

  constructor(
    private alert: RTEAlertService,
    private dragulaService: DragulaService,
    private differs: IterableDiffers) {

    super();

    this.differFilters = differs.find([]).create(null);
    this.differColumns = differs.find([]).create(null);
  }

  //#endregion

  //#region Interface implementations

  ngOnInit() {
    if (this.report && !this.report.Entities) {
      this.report.Entities = [];
    }

    this.columnDragulaGroup  = this.sessionId + 'COLUMN';
    this.linkGroupName = this.sessionId + 'RELATION';

    this.configureDragulaDragAndDrop();
    this.checkColumnAndFilters();
    this.reportBKP = newInstanceToObject(this.report);
    this.setRelevantProperties();
    this.filtersEntities();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.dragulaService.destroy(this.columnDragulaGroup);
  }

  ngDoCheck() {
    if (this.isBindingValuesChanged()) {
      setTimeout(() => {

        this.clearColumnsInEntities();
        this.columns.forEach(value => {
          this.bindColumnToEntity(value.ReportEntityId, value);
        });

        this.clearFiltersInEntities();
        this.filters.forEach(value => {
          this.bindFilterToEntity(value.ReportEntityId, value);
        });
      });
    }

    if (this.verifyChanges) {
      const compare = compareOnlyRelevantProperties(this.reportBKP, this.report, true, this.relevantProperties);
      const changes = compare.Changes;

      if (this.changes !== changes) {
        this.changes = changes;
        setTimeout(() => {
          this.changesChange.emit(this.changes);
        }, 0);
      }
    } else {
      this.reportBKP = newInstanceToObject(this.report);
    }
    this.filtersEntities();
  }

  filtersEntities() {
    this.entitiesEntity = this.entities.filter(x => x.Entity.EntityType === EntityTypeEnum.Entity);
  }

  setRelevantProperties(): void {
    this.relevantProperties = [
      new RelevantProperty('RelatedEntities', TypeEnum.Array, [
          new RelevantProperty('ColumnName'),
          new RelevantProperty('RelatedColumnName'),
          new RelevantProperty('JoinType')
        ]),
      new RelevantProperty('Entities', TypeEnum.Array, [
          new RelevantProperty('Alias'),
          new RelevantProperty('IsMain'),
          new RelevantProperty('Columns', TypeEnum.Array, [
              new RelevantProperty('IsGroupBy'),
              new RelevantProperty('Alias'),
              new RelevantProperty('TotalizerType')
          ]),
          new RelevantProperty('Filters', TypeEnum.Array, [
            new RelevantProperty('ReportEntityId'),
            new RelevantProperty('Type'),
            new RelevantProperty('Order'),
          ])
      ])
    ];
  }

  //#endregion

  //#region Methods

  isBindingValuesChanged() {
    return this.differColumns.diff(this.columns) ||
           this.differFilters.diff(this.filters);
  }

  getEntitybyId(id: number): ReportEntity {
    return this.report.Entities.find(value => {
      return value.Id === id;
    });
  }

  bindColumnToEntity(id: number, column: Column) {
    const entity = this.getEntitybyId(id);
    if (entity) {

      if (entity.Columns == null) {
        entity.Columns = [];
      }

      entity.Columns.push(column);
    }
  }

  bindFilterToEntity(id: number, filter: Filter) {
    const entity = this.getEntitybyId(id);
    if (entity) {

      if (entity.Filters == null) {
        entity.Filters = [];
      }

      entity.Filters.push(filter);
    }
  }

  checkColumnAndFilters() {
    this.columns = [];
    this.filters = [];

    this.report.Entities.forEach(entity => {
      entity.Columns.forEach(column => {
        column.ReportEntityId = entity.Id;
        this.columns.push(column);
      });

      entity.Filters.forEach(filter => {
        filter.ReportEntityId = entity.Id;
        this.filters.push(filter);
      });
    });

    this.columns = sortListByOrder(this.columns);
    this.filters = sortListByOrder(this.filters);
  }

  clearColumnsInEntities() {
    this.report.Entities.forEach(value => {
      value.Columns = [];
    });
  }

  clearFiltersInEntities() {
    this.report.Entities.forEach(value => {
      value.Filters = [];
    });
  }

  //#endregion

  //#endregion

  //#region Dragula Configurations

  configureDragulaDragAndDrop() {

    const groupAux = this.dragulaService.find(this.columnDragulaGroup);


    if (groupAux == null) {
      this.dragulaService.createGroup(this.columnDragulaGroup, {
        copy: (el) => {
          return el.classList.contains('item-detail');
        },
        copyItem: (column: Column) => {

          let obj: any = null;

          obj = {
            ReportEntityId: column.ReportEntityId,
            EntityId : column.EntityId,
            Name: column.Name,
            ColumnType: {
              SystemType : column.SystemType,
              DbType: column.Type
            },
            Required: false,
            MaxLength: null,
            Order : column.Order,
            IsGroupBy : false,
            Alias: !column.Alias ? column.Name : column.Alias,
            Legend: column.Name,
            Type: FilterTypeEnum.Equal,
            TotalizerType: TotalizerTypeEnum.None
          };

          if (obj.Alias) {
            obj.Alias = obj.Alias.toUpperCase();
          }

          if (obj.Legend) {
            obj.Legend = obj.Legend.toUpperCase();
          }

          this.emitChange(true);

          return obj;
        },
        accepts: (el, target) => {
          const classList = target.classList;
          const accept: boolean = classList.contains('mat-table');

          return accept;
        },
        moves: (el, target, handle) => {
          const canMove = handle.classList.contains('arrow') && !this.disabled;
          this.cdkDragDisabled = canMove;

          return canMove;
        }
      });

      this.subscription.add(
        this.dragulaService.dropModel(this.columnDragulaGroup).subscribe(drop => {
          this.onDropModel(drop);
        })
      );
    }
  }

  getOrderColumn(column: Column) {
    column.Order = this.columns.indexOf(column) + 1;
    return column.Order;
  }

  setAliasToReportEntity(alias: string) {
    let count = 0;

    this.report.Entities.forEach(value => {
      if (value.Alias === alias) {
        count ++;
      }
    });

    if (count > 0) {
      alias += '_' + (count + 1);
    }

    const founded = this.report.Entities.find(value => value.Alias === alias);

    if (founded) {
      alias = this.setAliasToReportEntity(alias);
    }

    return alias;
  }
  //#endregion

  //#region Click Events

  onFullScreenClick(event) {
    this.fullScreenState = this.fullScreenState ===  'default' ? 'fullscreen' : 'default';
    this.fullScreenStateChange.emit(this.fullScreenState);
  }

  //#endregion

  //#region Output Events

  emitChange(action: boolean) {
    this.onChange.emit(action);
  }

  onDeleteEntity(entity: ReportEntity) {
    this.checkColumnAndFilters();
  }

  onEntityAdd(entity: ReportEntity) {
    if (entity) {

      this.report.Entities.push({
        Id: parseInt((Math.random() * 100).toString()),
        Alias: this.setAliasToReportEntity(entity.Alias),
        Columns: [],
        Configuration : '',
        Entity: entity.Entity,
        EntityId: entity.EntityId,
        Filters: [],
        IsMain: this.report.Entities.length === 0,
        Type: FilterTypeEnum.Equal
      });

      this.emitChange(true);
    }
  }

  onDropModel(drop: any) {
    const model: any[] = drop.targetModel,
      item = drop.item,
      index = model.indexOf(item);

    let newIndex = model.length > 0 &&  (model.length - 1) !== index ? index - 1 : index;

    if (index !== -1) {
      const filter = model.filter((value, i)  => {
        return value.ReportEntityId === item.ReportEntityId && value.Name === item.Name && i !== index;
      });

      if (!!filter && filter.length > 0) {
        this.alert.warning(`A coluna ${item.Name} já foi adicionada`);
        model.splice(index, 1);
        this.emitChange(false);
      }
    }

    const groupQuantity = model.filter(x => x.IsGroupBy).length;

    if (!item.IsGroupBy && groupQuantity > 0 && groupQuantity > newIndex) {
      newIndex = groupQuantity;
    }

    if (newIndex !== index) {
      const aux = model[newIndex];

      model.splice(newIndex, 1, item);
      model.splice(newIndex + (index - newIndex), 1, aux);
    }
  }

  //#endregion
}
